import { useEffect, useState } from 'react';
import API from '../api';

// ── 조직구성(부서/직급) 관리 모달 ──────────────────────
// - 계정관리(AdminUsers) 화면 우측 상단 "조직구성" 버튼을 누르면 열립니다.
// - 최고관리자만 접근하는 화면이므로 이 컴포넌트 자체에는 별도 권한 체크가 없고,
//   백엔드(routes/org.js)에서 isSuperAdmin으로 막아둡니다.
// - 부서에는 "유형"(관리직/생산/품질), 직급에는 "등급"(임원/부서장/팀원)이 내부적으로 지정되며,
//   공지사항 공개범위(임원이상~사내전체) 계산 등에 쓰입니다.
//   → 새로 추가할 때는 이름만 입력하고, 유형/등급은 기본값으로 들어간 뒤 목록에서 "수정"으로 바꿉니다.
// - ▲▼ 로 목록 순서를 바꿀 수 있고, 이 순서가 계정관리의 부서/직급 선택 목록에도 그대로 반영됩니다.
// - "수정"을 누르면 이름 글자와 유형/등급을 함께 바로 고칠 수 있습니다.
//
// ── 저장 위치 ──
//   AdminUsers.js와 같은 폴더(frontend/src/pages/)에 파일명을 OrgStructureModal.js로 저장해주세요.
const DEPT_TYPES  = ['관리직', '생산', '품질'];
const RANK_LEVELS = ['임원', '부서장', '팀원'];

export default function OrgStructureModal({ onClose }) {
  // 목록순서(대표이사 → 부서 → 직급, 2026-08 요청)에 맞춰 기본 열림 탭도 대표이사로 맞춘다.
  const [tab, setTab] = useState('exec'); // 'dept' | 'position' | 'exec'
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  // ── 대표이사(경영진) — 부서/직급과 별개의 특수 목록. 조직도 상단에 독립된 박스로 표시된다.
  const [executives, setExecutives] = useState([]);
  const [execTitle, setExecTitle] = useState('대표이사');
  const [execName, setExecName] = useState('');
  const [execUserId, setExecUserId] = useState(''); // 등록된 직원 중 선택(선택사항) — 실제 로그인 계정과 연결
  const [editExecId, setEditExecId] = useState(null);
  const [editExecTitle, setEditExecTitle] = useState('');
  const [editExecName, setEditExecName] = useState('');
  const [editExecUserId, setEditExecUserId] = useState('');
  // 대표이사를 "직접입력"이 아니라 등록된 직원 중에서 골라 실제 로그인 계정과 연결할 수 있게
  // 하는 선택 목록 — 스토리지 백업 등, "대표이사에게 안내" 같은 기능이 실제 계정으로 알림을
  // 보낼 수 있으려면 이름 텍스트만으로는 부족하고 반드시 계정(user_id)이 있어야 하기 때문이다.
  const [employees, setEmployees] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([API.get('/org'), API.get('/org/executives'), API.get('/org/employees')])
      .then(([r, r2, r3]) => {
        setDepartments(r.data.departments || []);
        setPositions(r.data.positions || []);
        setExecutives(r2.data || []);
        setEmployees(r3.data || []);
      })
      .catch(() => setMsg('불러오기 실패'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const isDept = tab === 'dept';
  const isExec = tab === 'exec';
  const list = isDept ? departments : positions;
  const setList = isDept ? setDepartments : setPositions;
  const endpoint = isDept ? '/org/departments' : '/org/positions';
  const extraField = isDept ? 'dept_type' : 'rank_level';
  const extraOptions = isDept ? DEPT_TYPES : RANK_LEVELS;

  const switchTab = (key) => {
    setTab(key);
    setNewName('');
    setMsg('');
    setEditingId(null);
    setEditExecId(null);
  };

  // ── 대표이사 CRUD ──
  // 2026-08 요청: "이름을 직접 입력하는 칸을 지우고 직원을 바로 연결할 수 있게" — 자유 텍스트로
  // 이름만 입력해 등록하는 방식은 실제 로그인 계정과 연결되지 않아 "대표이사에게 안내" 같은
  // 기능의 알림 대상이 될 수 없고, 동명이인 등으로 인한 중복 표시 문제의 원인이기도 했다.
  // 이제 반드시 등록된 직원 중에서 선택해야만 추가할 수 있다(직함만 자유롭게 커스터마이즈 가능).
  const handleAddExec = async () => {
    if (!execUserId) { setMsg('직원을 선택해주세요.'); return; }
    setSaving(true);
    try {
      await API.post('/org/executives', {
        title: execTitle.trim() || '대표이사',
        user_id: execUserId,
      });
      setExecName('');
      setExecUserId('');
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '추가 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };
  const startEditExec = (item) => {
    setMsg('');
    setEditExecId(item.id);
    setEditExecTitle(item.title || '대표이사');
    setEditExecName(item.name || '');
    setEditExecUserId(item.user_id || '');
  };
  const saveEditExec = async (id) => {
    if (!editExecUserId) { setMsg('직원을 선택해주세요.'); return; }
    try {
      await API.patch(`/org/executives/${id}`, {
        title: editExecTitle.trim() || '대표이사',
        user_id: editExecUserId,
      });
      setEditExecId(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '수정 중 오류가 발생했습니다.');
    }
  };
  // 직원선택 드롭다운에서 선택하면 성명 입력칸을 그 계정 이름으로 맞춰준다(연결되면 백엔드가
  // 어차피 계정 이름으로 고정 저장하지만, 저장 전 미리보기 목적으로 프론트에서도 동기화).
  const onPickExecEmployee = (uid, which) => {
    const emp = employees.find(e => String(e.id) === String(uid));
    if (which === 'add') {
      setExecUserId(uid);
      if (emp) setExecName(emp.name);
    } else {
      setEditExecUserId(uid);
      if (emp) setEditExecName(emp.name);
    }
  };
  const handleDeleteExec = async (id, title, name) => {
    if (!window.confirm(`"${title}${name ? ' ' + name : ''}"을(를) 삭제하시겠습니까?\n조직도에 있던 연결선도 함께 삭제됩니다.`)) return;
    try {
      await API.delete(`/org/executives/${id}`);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };
  const handleMoveExec = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= executives.length) return;
    const newList = [...executives];
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setExecutives(newList);
    try {
      await API.patch('/org/executives/reorder', { ids: newList.map(i => i.id) });
    } catch (err) {
      setMsg(err.response?.data?.message || '순서 변경 중 오류가 발생했습니다.');
      load();
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await API.post(endpoint, { name });
      setNewName('');
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '추가 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}"을(를) 삭제하시겠습니까?\n이미 사용자에게 배정되어 있어도 그 값은 그대로 남고, 목록에서만 제거됩니다.`)) return;
    try {
      await API.delete(`${endpoint}/${id}`);
      if (editingId === id) setEditingId(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  const startEdit = (item) => {
    setMsg('');
    setEditingId(item.id);
    setEditName(item.name);
  };

  // 이름만 저장 (유형/등급은 기본값 그대로 유지 — 화면에는 노출하지 않음)
  const saveEdit = async (id) => {
    const name = editName.trim();
    if (!name) { setMsg('이름을 입력해주세요.'); return; }
    try {
      await API.patch(`${endpoint}/${id}`, { name });
      setEditingId(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || '수정 중 오류가 발생했습니다.');
    }
  };

  // ▲▼ 순서 변경 (낙관적 업데이트 후 실패 시 서버 재조회로 원복)
  const handleMove = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= list.length) return;
    const newList = [...list];
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setList(newList);
    try {
      await API.patch(`${endpoint}/reorder`, { ids: newList.map(i => i.id) });
    } catch (err) {
      setMsg(err.response?.data?.message || '순서 변경 중 오류가 발생했습니다.');
      load();
    }
  };

  const tabBtn = (key, label) => (
    <button onClick={() => switchTab(key)}
      style={{ flex:1, padding:'10px 0', background:'none', border:'none',
        borderBottom: tab===key ? '2px solid #185FA5' : '2px solid transparent',
        cursor:'pointer', fontSize:14, fontWeight: tab===key ? 700 : 500,
        color: tab===key ? '#185FA5' : '#888' }}>
      {label}
    </button>
  );

  const arrowBtnStyle = (disabled) => ({
    background:'none', border:'none', padding:'0 4px', fontSize:11, lineHeight:'12px',
    color: disabled ? '#ddd' : '#888', cursor: disabled ? 'default' : 'pointer'
  });

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:480, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🏢 조직구성</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {/* 목록순서: 대표이사 → 부서 → 직급 (2026-08 요청) */}
        <div style={{ display:'flex', margin:'14px 24px 0', borderBottom:'1.5px solid #eee' }}>
          {tabBtn('exec', '대표이사')}
          {tabBtn('dept', '부서')}
          {tabBtn('position', '직급')}
        </div>

        {isExec ? (
          <div style={{ padding:'16px 24px' }}>
            <div style={{ fontSize:12, color:'#999', marginBottom:10 }}>
              대표이사(경영진)는 부서와 별개로 관리되며, 조직도에서 독립된 박스로 표시됩니다.
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:8 }}>
              <input value={execTitle} onChange={e => setExecTitle(e.target.value)}
                placeholder="직함 (예: 대표이사)"
                style={{ width:120, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                  fontSize:14, boxSizing:'border-box', outline:'none' }} />
              <select value={execUserId} onChange={e => onPickExecEmployee(e.target.value, 'add')}
                style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                  fontSize:14, boxSizing:'border-box', outline:'none', background:'#fff' }}>
                <option value="">— 직원을 선택하세요 —</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {[emp.department, emp.position, emp.name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
              <button onClick={handleAddExec} disabled={saving || !execUserId}
                style={{ padding:'9px 16px', background: execUserId ? '#185FA5' : '#ccc', color:'#fff',
                  border:'none', borderRadius:8, fontSize:13, fontWeight:600,
                  cursor: execUserId ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
                + 추가
              </button>
            </div>
            <div style={{ fontSize:11, color:'#aaa', marginTop:-2, marginBottom:14 }}>
              등록된 직원 중에서 선택해 계정과 연결해야 추가할 수 있습니다(성명 직접입력은
              더 이상 지원하지 않습니다). 연결된 계정은 스토리지 백업 등 "대표이사에게 안내"
              기능의 알림 대상이 됩니다.
            </div>

            {msg && <div style={{ fontSize:13, color:'#dc2626', marginBottom:10 }}>{msg}</div>}

            <div style={{ border:'1px solid #eee', borderRadius:8, maxHeight:340, overflowY:'auto' }}>
              {loading ? (
                <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>불러오는 중...</div>
              ) : executives.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>등록된 대표이사가 없습니다.</div>
              ) : (
                executives.map((item, idx) => (
                  <div key={item.id}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                      padding:'10px 14px', borderTop: idx===0 ? 'none' : '1px solid #f0f0f0', gap:8 }}>
                    {editExecId === item.id ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:6, flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <input value={editExecTitle} onChange={e => setEditExecTitle(e.target.value)}
                            style={{ width:100, padding:'6px 8px', border:'1px solid #185FA5', borderRadius:6,
                              fontSize:13, outline:'none', boxSizing:'border-box' }} />
                          <select value={editExecUserId} onChange={e => onPickExecEmployee(e.target.value, 'edit')}
                            autoFocus
                            style={{ flex:1, padding:'6px 8px', border:'1px solid #185FA5', borderRadius:6,
                              fontSize:12, outline:'none', boxSizing:'border-box', background:'#fff' }}>
                            <option value="">— 직원을 선택하세요 —</option>
                            {employees.map(emp => (
                              <option key={emp.id} value={emp.id}>
                                {[emp.department, emp.position, emp.name].filter(Boolean).join(' ')}
                              </option>
                            ))}
                          </select>
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                            <button onClick={() => saveEditExec(item.id)}
                              style={{ background:'#185FA5', border:'none', color:'#fff', fontSize:12,
                                padding:'4px 10px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                              저장
                            </button>
                            <button onClick={() => setEditExecId(null)}
                              style={{ background:'none', border:'none', color:'#888', fontSize:12, cursor:'pointer' }}>
                              취소
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span style={{ fontSize:14 }}>
                          👑 {item.title}{item.name ? ` · ${item.name}` : ''}
                          {item.user_id ? (
                            <span style={{ marginLeft:6, fontSize:11, color:'#185FA5', background:'#eaf2fb',
                              padding:'2px 6px', borderRadius:4 }}>🔗 계정연결됨</span>
                          ) : null}
                        </span>
                        <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
                          <div style={{ display:'flex', flexDirection:'column' }}>
                            <button disabled={idx === 0} onClick={() => handleMoveExec(idx, -1)}
                              style={arrowBtnStyle(idx === 0)}>▲</button>
                            <button disabled={idx === executives.length - 1} onClick={() => handleMoveExec(idx, 1)}
                              style={arrowBtnStyle(idx === executives.length - 1)}>▼</button>
                          </div>
                          <button onClick={() => startEditExec(item)}
                            style={{ background:'none', border:'none', color:'#185FA5', fontSize:12,
                              cursor:'pointer', fontWeight:600 }}>
                            수정
                          </button>
                          <button onClick={() => handleDeleteExec(item.id, item.title, item.name)}
                            style={{ background:'none', border:'none', color:'#dc2626', fontSize:12,
                              cursor:'pointer', fontWeight:600 }}>
                            삭제
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
        <div style={{ padding:'16px 24px' }}>
          <div style={{ display:'flex', gap:8, marginBottom:14 }}>
            <input value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder={isDept ? '새 부서명 입력 (예: 영업1팀)' : '새 직급명 입력 (예: 대리)'}
              style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                fontSize:14, boxSizing:'border-box', outline:'none' }} />
            <button onClick={handleAdd} disabled={saving || !newName.trim()}
              style={{ padding:'9px 16px', background: newName.trim() ? '#185FA5' : '#ccc', color:'#fff',
                border:'none', borderRadius:8, fontSize:13, fontWeight:600,
                cursor: newName.trim() ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
              + 추가
            </button>
          </div>

          {msg && <div style={{ fontSize:13, color:'#dc2626', marginBottom:10 }}>{msg}</div>}

          <div style={{ border:'1px solid #eee', borderRadius:8, maxHeight:340, overflowY:'auto' }}>
            {loading ? (
              <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>불러오는 중...</div>
            ) : list.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>
                등록된 {isDept ? '부서' : '직급'}가 없습니다.
              </div>
            ) : (
              list.map((item, idx) => (
                <div key={item.id}
                  style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'10px 14px', borderTop: idx===0 ? 'none' : '1px solid #f0f0f0', gap:8 }}>

                  {editingId === item.id ? (
                    <>
                      <input value={editName} onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveEdit(item.id)}
                        autoFocus
                        style={{ flex:1, padding:'6px 8px', border:'1px solid #185FA5', borderRadius:6,
                          fontSize:13, outline:'none', boxSizing:'border-box' }} />
                      <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                        <button onClick={() => saveEdit(item.id)}
                          style={{ background:'#185FA5', border:'none', color:'#fff', fontSize:12,
                            padding:'4px 10px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                          저장
                        </button>
                        <button onClick={() => setEditingId(null)}
                          style={{ background:'none', border:'none', color:'#888', fontSize:12, cursor:'pointer' }}>
                          취소
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize:14 }}>{item.name}</span>
                      <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
                        <div style={{ display:'flex', flexDirection:'column' }}>
                          <button disabled={idx === 0} onClick={() => handleMove(idx, -1)}
                            style={arrowBtnStyle(idx === 0)}>▲</button>
                          <button disabled={idx === list.length - 1} onClick={() => handleMove(idx, 1)}
                            style={arrowBtnStyle(idx === list.length - 1)}>▼</button>
                        </div>
                        <button onClick={() => startEdit(item)}
                          style={{ background:'none', border:'none', color:'#185FA5', fontSize:12,
                            cursor:'pointer', fontWeight:600 }}>
                          수정
                        </button>
                        <button onClick={() => handleDelete(item.id, item.name)}
                          style={{ background:'none', border:'none', color:'#dc2626', fontSize:12,
                            cursor:'pointer', fontWeight:600 }}>
                          삭제
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        )}

        <div style={{ padding:'0 24px 20px' }}>
          <button onClick={onClose}
            style={{ width:'100%', padding:'11px', background:'#f0f0f0', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}